import { useAnalytics } from 'App/Services/Analytics';

import ball1920 from '../../../../../assets/hero_ball-1920.png';
import ball576 from '../../../../../assets/hero_ball_576.png';
import ball768 from '../../../../../assets/hero_ball_768.png';
import ball992 from '../../../../../assets/hero_ball_992.png';
import footbolist1920 from '../../../../../assets/hero_player-1920.png';
import footbolist576 from '../../../../../assets/hero_player_576.png';
import footbolist768 from '../../../../../assets/hero_player_768.png';
import footbolist992 from '../../../../../assets/hero_player_992.png';
import lightning1920 from '../../../../../assets/lightning-hero_1920.png';
import lightning360 from '../../../../../assets/lightning-hero_360.png';
import lightning414 from '../../../../../assets/lightning-hero_414.png';
import lightning576 from '../../../../../assets/lightning-hero_576.png';
import lightning768 from '../../../../../assets/lightning-hero_768.png';
import Header from '../../../../../components/Header/Header';
import polyglot from '../../../../../localization';
import { LandingButton } from '../../LandingButton';
import CloudBackground from '../LightningCloudBackground';
import { LightningHeroBenefits } from '../LightningHeroBenefits';
import { LightningHeroCheckbox } from '../LightningHeroCheckbox';
import { LightningHeroPhone } from '../LightningHeroPhone';

import './styles.scss';

/**
 * LightningIntro renders the top of the lightning hero: layered artwork, header,
 * localized title, Play CTA, age checkbox, desktop benefits, and the phone
 * mockup with video.
 *
 * @param {string} lang — Language key used to localize intro copy and alts.
 * @param {boolean} consentBannerReady — When false, the Play button stays disabled.
 * @param {() => void} onBtnClick — Handler for the primary Play CTA.
 * @param {string} [videoUrl] — LP video URL for the phone fallback player.
 * @param {boolean} checked — Age-confirmation checkbox state.
 * @param {Function} setChecked — Setter paired with `checked`.
 */
export const LightningIntro = ({
    lang,
    consentBannerReady,
    onBtnClick,
    videoUrl,
    checked,
    setChecked,
}) => {
    const { sendGameDesignEvent, sendGoogleBasicEvent } = useAnalytics();

    const handleCheckboxChange = () => {
        setChecked((prevChecked) => {
            const nextChecked = !prevChecked;

            sendGameDesignEvent(
                `HP:AgeVerificationCheckBox:${nextChecked ? 'True' : 'False'}`
            );
            sendGoogleBasicEvent(
                'hp',
                `age_verification_checkbox_${nextChecked ? 'true' : 'false'}`
            );

            return nextChecked;
        });
    };

    return (
        <div className='lightning-hero-intro'>
            <div className='lightning-hero-intro__ball lightning-hero-intro__img'>
                <picture>
                    <source srcSet={ball1920} media='(min-width: 1400px)' />
                    <source srcSet={ball992} media='(min-width: 992px)' />
                    <source srcSet={ball768} media='(min-width: 768px)' />
                    <img
                        src={ball576}
                        width={371}
                        height={761}
                        alt={polyglot.t(`${lang}.howPlay.alt.step1`)}
                    />
                </picture>
            </div>
            <Header phase='landing' />

            <div className='lightning-hero-intro__lightning'>
                <picture>
                    <source
                        srcSet={lightning1920}
                        media='(min-width: 1400px)'
                    />
                    <source srcSet={lightning768} media='(min-width: 768px)' />
                    <source srcSet={lightning576} media='(min-width: 576px)' />
                    <source srcSet={lightning414} media='(min-width: 414px)' />
                    <img
                        src={lightning360}
                        width={180}
                        height={36}
                        alt={polyglot.t(`${lang}.hero.alt.lightning`)}
                    />
                </picture>
            </div>
            <div className='lightning-hero-intro__cloud-wrap'>
                <div className='lightning-hero-intro__cloud'>
                    <div className='lightning-hero-intro__bg'>
                        <CloudBackground
                            desktopClass='lightning-hero-intro__bg-desktop'
                            mobileClass='lightning-hero-intro__bg-mobile'
                        />
                    </div>
                </div>
                <div className='lightning-hero-intro__wrapper'>
                    <div className='lightning-hero-intro__player lightning-hero-intro__img'>
                        <picture>
                            <source
                                srcSet={footbolist1920}
                                media='(min-width: 1400px)'
                            />
                            <source
                                srcSet={footbolist992}
                                media='(min-width: 992px)'
                            />
                            <source
                                srcSet={footbolist768}
                                media='(min-width: 768px)'
                            />
                            <img
                                src={footbolist576}
                                width={326}
                                height={500}
                                alt={polyglot.t(`${lang}.howPlay.alt.step1`)}
                            />
                        </picture>
                    </div>
                    <div className='lightning-hero-intro__content'>
                        <LandingButton
                            disabled={!consentBannerReady}
                            onClick={onBtnClick}
                            lang={lang}
                            btnClassName='lightning-hero-intro__btn lightning-hero__desktop'
                            textClassName='lightning-hero-intro__btn-text'
                            alt={polyglot.t(`${lang}.hero.alt.btnPlay`)}
                            isLightning
                        />

                        <LightningHeroCheckbox
                            lang={lang}
                            checked={checked}
                            onChange={handleCheckboxChange}
                            altText={polyglot.t(`${lang}.hero.alt.checkbox`)}
                            className='lightning-hero__desktop'
                        />

                        <div className='lightning-hero-intro__benefits'>
                            <LightningHeroBenefits lang={lang} />
                        </div>
                    </div>

                    <LightningHeroPhone lang={lang} videoUrl={videoUrl} />

                    <h1 className='lightning-hero-intro__title'>
                        {polyglot.t(`${lang}.hero.titleLightning`)}
                    </h1>

                    <LandingButton
                        disabled={!consentBannerReady}
                        btnClassName='lightning-hero-intro__btn lightning-hero__mobile'
                        textClassName='lightning-hero-intro__btn-text'
                        onClick={onBtnClick}
                        lang={lang}
                        alt={polyglot.t(`${lang}.hero.alt.btnPlay`)}
                        isLightning
                    />
                    <LightningHeroCheckbox
                        lang={lang}
                        checked={checked}
                        altText={polyglot.t(`${lang}.hero.alt.checkbox`)}
                        onChange={handleCheckboxChange}
                        className='lightning-hero__mobile'
                    />
                </div>
            </div>
        </div>
    );
};
